/**
 * SubFeatures - Definições de subfuncionalidades para controle granular de permissões
 * Cada módulo (StoreFeature) possui subfuncionalidades que podem ser habilitadas/desabilitadas individualmente
 */

import { StoreFeature } from './networkUser';

/**
 * Enum com todos os IDs de subfuncionalidades
 * Os IDs correspondem aos 'id' definidos em menuItems.ts
 */
export enum SubFeature {
  // INICIO
  INICIO_HOME = 'inicio-home',
  INICIO_DASHBOARD = 'inicio-dashboard',
  INICIO_NOVIDADES = 'inicio-novidades',
  INICIO_NOTIFICACOES = 'inicio-notificacoes',
  INICIO_MINHA_CONTA = 'inicio-minha-conta',
  INICIO_AJUDA = 'inicio-ajuda',
  
  // PAINEL
  PAINEL_RESUMO = 'painel-resumo',
  PAINEL_VENDAS = 'painel-vendas',
  PAINEL_ESTOQUE = 'painel-estoque',
  PAINEL_FINANCEIRO = 'painel-financeiro',

  // ESTOQUE
  ESTOQUE_LISTA = 'estoque-lista',
  VITRINE_LISTA = 'vitrine-lista',
  ESTOQUE_PAINEL = 'estoque-painel',
  ESTOQUE_VENDIDOS = 'estoque-vendidos',
  ESTOQUE_MANUTENCAO = 'estoque-manutencao',
  ESTOQUE_LUCRATIVIDADE = 'estoque-lucratividade',
  ESTOQUE_CHECKLIST = 'estoque-checklist',
  ESTOQUE_CHECKLIST_MANUTENCAO = 'estoque-checklist-manutencao',
  ESTOQUE_ANUNCIOS = 'estoque-anuncios',
  
  // PESSOAS (antigo CLIENTES)
  PESSOAS_LISTA = 'pessoas-lista',
  PESSOAS_NOVO = 'pessoas-novo',
  PESSOAS_ANIVERSARIANTES = 'pessoas-aniversariantes',
  PESSOAS_RANKING = 'pessoas-ranking',
  PESSOAS_INATIVOS = 'pessoas-inativos',
  PESSOAS_ACESSO_TOTAL_LOJA = 'pessoas-acesso-total-loja',
  PESSOAS_FORNECEDORES = 'pessoas-fornecedores',
  
  // Aliases legados para compatibilidade
  /** @deprecated Use PESSOAS_LISTA */
  CLIENTES_LISTA = 'pessoas-lista',
  /** @deprecated Use PESSOAS_NOVO */
  CLIENTES_NOVO = 'pessoas-novo',
  /** @deprecated Use PESSOAS_ANIVERSARIANTES */
  CLIENTES_ANIVERSARIANTES = 'pessoas-aniversariantes',
  /** @deprecated Use PESSOAS_RANKING */
  CLIENTES_RANKING = 'pessoas-ranking',
  /** @deprecated Use PESSOAS_INATIVOS */
  CLIENTES_INATIVOS = 'pessoas-inativos',
  /** @deprecated Use PESSOAS_ACESSO_TOTAL_LOJA */
  CLIENTES_ACESSO_TOTAL_LOJA = 'pessoas-acesso-total-loja',
  
  // VENDAS
  VENDAS_LISTA = 'vendas-lista',
  VENDAS_RESERVAS = 'vendas-reservas',
  VENDAS_DOCUMENTACAO = 'vendas-documentacao',
  VENDAS_ABAIXO_MINIMO = 'vendas-abaixo-minimo',
  
  // MARKETING
  MARKETING_SITE = 'marketing-site',
  
  // CRM
  CRM_DASHBOARD = 'crm-dashboard',
  CRM_LEADS = 'crm-leads',
  CRM_AGENDA = 'crm-agenda',
  CRM_PIPELINE = 'crm-pipeline',
  CRM_INBOX = 'crm-inbox',
  CRM_CHANNELS = 'crm-channels',
  CRM_TEMPLATES = 'crm-templates',
  CRM_CONFIG = 'crm-config',
  CRM_ARMAZENAMENTO = 'crm-armazenamento',
  
  // TAREFAS
  TAREFAS_TODAS = 'tarefas-todas',
  TAREFAS_MINHAS = 'tarefas-minhas',
  TAREFAS_PENDENTES = 'tarefas-pendentes',
  TAREFAS_VENCIDAS = 'tarefas-vencidas',
  TAREFAS_CONCLUIDAS = 'tarefas-concluidas',
  
  // FINANCEIRO
  FIN_DASHBOARD = 'fin-dashboard',
  FIN_CONTAS = 'fin-contas',
  FIN_MOVIMENTACOES = 'fin-movimentacoes',
  FIN_DESPESAS = 'fin-despesas',
  FIN_COMISSOES = 'fin-comissoes',
  FIN_RELATORIOS = 'fin-relatorios',
  FIN_PREVISOES = 'fin-previsoes',
  
  // FERRAMENTAS
  TOOLS_SIMULACOES = 'tools-simulacoes',
  /** @deprecated Movido para PESSOAS_FORNECEDORES */
  TOOLS_FORNECEDORES = 'pessoas-fornecedores',
  
  // GERENCIAMENTO (feature EQUIPE + CONFIGURACOES)
  GERENCIAR_USUARIOS = 'gerenciar-usuarios',
  GERENCIAR_LOJAS = 'gerenciar-lojas',
  GERENCIAR_PRESETS = 'gerenciar-presets',
  GERENCIAR_ROLE_PRESETS = 'gerenciar-role-presets',
  GERENCIAR_CANAIS_VENDA = 'gerenciar-canais-venda',
  GERENCIAR_CONTRATOS = 'gerenciar-contratos',
  GERENCIAR_CONFIG = 'gerenciar-config',
}

/**
 * Labels para exibição das subfuncionalidades
 */
export const SUBFEATURE_LABELS: Record<SubFeature, string> = {
  // INICIO
  [SubFeature.INICIO_HOME]: 'Início',
  [SubFeature.INICIO_DASHBOARD]: 'Dashboard',
  [SubFeature.INICIO_NOVIDADES]: 'Novidades',
  [SubFeature.INICIO_NOTIFICACOES]: 'Notificações',
  [SubFeature.INICIO_MINHA_CONTA]: 'Minha Conta',
  [SubFeature.INICIO_AJUDA]: 'Central de Ajuda',

  // PAINEL
  [SubFeature.PAINEL_RESUMO]: 'Resumo',
  [SubFeature.PAINEL_VENDAS]: 'Vendas',
  [SubFeature.PAINEL_ESTOQUE]: 'Estoque',
  [SubFeature.PAINEL_FINANCEIRO]: 'Financeiro',
  
  // ESTOQUE
  [SubFeature.ESTOQUE_LISTA]: 'Estoque',
  [SubFeature.VITRINE_LISTA]: 'Vitrine',
  [SubFeature.ESTOQUE_PAINEL]: 'Painel Analítico',
  [SubFeature.ESTOQUE_VENDIDOS]: 'Vendidos',
  [SubFeature.ESTOQUE_MANUTENCAO]: 'Manutenção',
  [SubFeature.ESTOQUE_LUCRATIVIDADE]: 'Lucratividade',
  [SubFeature.ESTOQUE_CHECKLIST]: 'Checklist',
  [SubFeature.ESTOQUE_CHECKLIST_MANUTENCAO]: 'Checklist Manutenção',
  [SubFeature.ESTOQUE_ANUNCIOS]: 'Anúncios',
  
  // PESSOAS
  [SubFeature.PESSOAS_LISTA]: 'Lista de Pessoas',
  [SubFeature.PESSOAS_NOVO]: 'Nova Pessoa',
  [SubFeature.PESSOAS_ANIVERSARIANTES]: 'Aniversariantes',
  [SubFeature.PESSOAS_RANKING]: 'Melhores Pessoas',
  [SubFeature.PESSOAS_INATIVOS]: 'Inativos',
  [SubFeature.PESSOAS_ACESSO_TOTAL_LOJA]: 'Acesso a todas as pessoas da loja',
  [SubFeature.PESSOAS_FORNECEDORES]: 'Fornecedores',
  
  // VENDAS
  [SubFeature.VENDAS_LISTA]: 'Vendas',
  [SubFeature.VENDAS_RESERVAS]: 'Reservas',
  [SubFeature.VENDAS_DOCUMENTACAO]: 'Documentação',
  [SubFeature.VENDAS_ABAIXO_MINIMO]: 'Vender Abaixo do Preço Mínimo',
  
  // MARKETING
  [SubFeature.MARKETING_SITE]: 'Site da Loja',
  
  // CRM
  [SubFeature.CRM_DASHBOARD]: 'Dashboard CRM',
  [SubFeature.CRM_LEADS]: 'Leads',
  [SubFeature.CRM_AGENDA]: 'Agenda',
  [SubFeature.CRM_PIPELINE]: 'Funil de Vendas',
  [SubFeature.CRM_INBOX]: 'Chat / Inbox',
  [SubFeature.CRM_CHANNELS]: 'Canais',
  [SubFeature.CRM_TEMPLATES]: 'Templates de Mensagem',
  [SubFeature.CRM_CONFIG]: 'Configurações do CRM',
  [SubFeature.CRM_ARMAZENAMENTO]: 'Armazenamento',
  
  // TAREFAS
  [SubFeature.TAREFAS_TODAS]: 'Todas as Tarefas',
  [SubFeature.TAREFAS_MINHAS]: 'Minhas Tarefas',
  [SubFeature.TAREFAS_PENDENTES]: 'Pendentes',
  [SubFeature.TAREFAS_VENCIDAS]: 'Vencidas',
  [SubFeature.TAREFAS_CONCLUIDAS]: 'Concluídas',
  
  // FINANCEIRO
  [SubFeature.FIN_DASHBOARD]: 'Dashboard',
  [SubFeature.FIN_CONTAS]: 'Contas Bancárias',
  [SubFeature.FIN_MOVIMENTACOES]: 'Movimentações',
  [SubFeature.FIN_DESPESAS]: 'Despesas Recorrentes',
  [SubFeature.FIN_COMISSOES]: 'Comissões',
  [SubFeature.FIN_RELATORIOS]: 'Relatórios',
  [SubFeature.FIN_PREVISOES]: 'Previsões',
  
  // FERRAMENTAS
  [SubFeature.TOOLS_SIMULACOES]: 'Simulações',
  // TOOLS_FORNECEDORES foi movido para PESSOAS_FORNECEDORES
  
  // GERENCIAMENTO
  [SubFeature.GERENCIAR_USUARIOS]: 'Usuários',
  [SubFeature.GERENCIAR_LOJAS]: 'Lojas',
  [SubFeature.GERENCIAR_PRESETS]: 'Presets de Veículos',
  [SubFeature.GERENCIAR_ROLE_PRESETS]: 'Cargos e Permissões',
  [SubFeature.GERENCIAR_CANAIS_VENDA]: 'Canais de Venda',
  [SubFeature.GERENCIAR_CONTRATOS]: 'Contratos',
  [SubFeature.GERENCIAR_CONFIG]: 'Configurações',
};

/**
 * Mapeamento de cada módulo (StoreFeature) para suas subfuncionalidades
 */
export const FEATURE_SUBFEATURES: Record<StoreFeature, SubFeature[]> = {
  [StoreFeature.INICIO]: [
    SubFeature.INICIO_HOME,
    SubFeature.INICIO_NOVIDADES,
    SubFeature.INICIO_NOTIFICACOES,
    SubFeature.INICIO_MINHA_CONTA,
    SubFeature.INICIO_AJUDA,
  ],
  [StoreFeature.VITRINE]: [
    SubFeature.VITRINE_LISTA,
  ],
  [StoreFeature.ESTOQUE]: [
    SubFeature.ESTOQUE_PAINEL,
    SubFeature.ESTOQUE_VENDIDOS,
    SubFeature.ESTOQUE_MANUTENCAO,
    SubFeature.ESTOQUE_LUCRATIVIDADE,
    
    SubFeature.ESTOQUE_CHECKLIST,
    SubFeature.ESTOQUE_ANUNCIOS,
  ],
  [StoreFeature.PESSOAS]: [
    SubFeature.PESSOAS_LISTA,
    SubFeature.PESSOAS_NOVO,
    SubFeature.PESSOAS_ANIVERSARIANTES,
    SubFeature.PESSOAS_RANKING,
    SubFeature.PESSOAS_INATIVOS,
    SubFeature.PESSOAS_ACESSO_TOTAL_LOJA,
    SubFeature.PESSOAS_FORNECEDORES,
  ],
  [StoreFeature.VENDAS]: [
    SubFeature.VENDAS_LISTA,
    SubFeature.VENDAS_RESERVAS,
    SubFeature.VENDAS_DOCUMENTACAO,
    SubFeature.VENDAS_ABAIXO_MINIMO,
  ],
  [StoreFeature.RESERVAS]: [
    SubFeature.VENDAS_RESERVAS,
  ],
  [StoreFeature.DOCUMENTACAO]: [
    SubFeature.VENDAS_DOCUMENTACAO,
  ],
  [StoreFeature.MARKETING]: [
    SubFeature.MARKETING_SITE,
  ],
  [StoreFeature.CRM]: [
    SubFeature.CRM_DASHBOARD,
    SubFeature.CRM_LEADS,
    SubFeature.CRM_AGENDA,
    SubFeature.CRM_PIPELINE,
    SubFeature.CRM_INBOX,
    SubFeature.CRM_CHANNELS,
    SubFeature.CRM_TEMPLATES,
    SubFeature.CRM_CONFIG,
    SubFeature.CRM_ARMAZENAMENTO,
  ],
  [StoreFeature.TAREFAS]: [
    SubFeature.TAREFAS_TODAS,
    SubFeature.TAREFAS_MINHAS,
    SubFeature.TAREFAS_PENDENTES,
    SubFeature.TAREFAS_VENCIDAS,
    SubFeature.TAREFAS_CONCLUIDAS,
  ],
  [StoreFeature.FINANCEIRO]: [
    SubFeature.FIN_DASHBOARD,
    SubFeature.FIN_CONTAS,
    SubFeature.FIN_MOVIMENTACOES,
    SubFeature.FIN_DESPESAS,
    SubFeature.FIN_COMISSOES,
    SubFeature.FIN_RELATORIOS,
    SubFeature.FIN_PREVISOES,
  ],
  [StoreFeature.FERRAMENTAS]: [
    SubFeature.TOOLS_SIMULACOES,
  ],
  [StoreFeature.FORNECEDORES]: [
    SubFeature.PESSOAS_FORNECEDORES,
  ],
  [StoreFeature.DASHBOARD]: [
    SubFeature.PAINEL_RESUMO,
    SubFeature.PAINEL_VENDAS,
    SubFeature.PAINEL_ESTOQUE,
    SubFeature.PAINEL_FINANCEIRO,
    // Legado: mantido para não invalidar permissões já salvas
    SubFeature.INICIO_DASHBOARD,
  ],
  [StoreFeature.AUDITORIA]: [], // Sem subitems por enquanto
  // Módulo unificado de gerenciamento
  [StoreFeature.GERENCIAR]: [
    SubFeature.GERENCIAR_USUARIOS,
    SubFeature.GERENCIAR_LOJAS,
    SubFeature.GERENCIAR_PRESETS,
    SubFeature.GERENCIAR_ROLE_PRESETS,
    SubFeature.GERENCIAR_CANAIS_VENDA,
    SubFeature.GERENCIAR_CONTRATOS,
    SubFeature.GERENCIAR_CONFIG,
  ],
  // Legados (mantidos para compatibilidade)
  [StoreFeature.EQUIPE]: [
    SubFeature.GERENCIAR_USUARIOS,
  ],
  [StoreFeature.CONFIGURACOES]: [
    SubFeature.GERENCIAR_USUARIOS,
    SubFeature.GERENCIAR_LOJAS,
    SubFeature.GERENCIAR_PRESETS,
    SubFeature.GERENCIAR_ROLE_PRESETS,
    SubFeature.GERENCIAR_CANAIS_VENDA,
    SubFeature.GERENCIAR_CONTRATOS,
    SubFeature.GERENCIAR_CONFIG,
  ],
};

/**
 * Retorna todas as subfuncionalidades de um módulo
 */
export const getSubFeaturesForFeature = (feature: StoreFeature): SubFeature[] => {
  return FEATURE_SUBFEATURES[feature] || [];
};

/**
 * Retorna todas as subfuncionalidades de múltiplos módulos
 */
export const getAllSubFeaturesForFeatures = (features: StoreFeature[]): SubFeature[] => {
  const subFeatures = new Set<SubFeature>();
  features.forEach(feature => {
    getSubFeaturesForFeature(feature).forEach(sf => subFeatures.add(sf));
  });
  return Array.from(subFeatures);
};

/**
 * Encontra o módulo pai de uma subfuncionalidade
 */
export const getParentFeature = (subFeature: SubFeature): StoreFeature | null => {
  for (const [feature, subFeatures] of Object.entries(FEATURE_SUBFEATURES)) {
    if (subFeatures.includes(subFeature)) {
      return feature as StoreFeature;
    }
  }
  return null;
};

/**
 * Verifica se um ID de string é uma subfuncionalidade válida
 */
export const isValidSubFeature = (id: string): id is SubFeature => {
  return Object.values(SubFeature).includes(id as SubFeature);
};

// Mapeamento de subfuncionalidades que são filhas de outras (hierarquia visual)
export const SUBFEATURE_CHILDREN: Partial<Record<SubFeature, SubFeature[]>> = {
  [SubFeature.VENDAS_LISTA]: [SubFeature.VENDAS_ABAIXO_MINIMO],
};

// Função helper para verificar se é item filho
export const isChildSubFeature = (subFeature: SubFeature): boolean => {
  return Object.values(SUBFEATURE_CHILDREN).flat().includes(subFeature);
};

// Função helper para obter filhos de uma subfuncionalidade
export const getSubFeatureChildren = (subFeature: SubFeature): SubFeature[] => {
  return SUBFEATURE_CHILDREN[subFeature] || [];
};

// Helper para verificar se um resultado contém uma SubFeature
export const hasSubFeatureInResult = (result: SubFeature[] | undefined | null, subFeature: SubFeature): boolean => {
  return result?.includes(subFeature) ?? false;
};
